<template>
  <megabit-base-dialog :show-dialog="showGuestDialog">
    <template v-slot:body>
      <v-card dark class="pt-4 pb-2">
        <v-card-title class="align-self-center">
          Login {{ $t('labels.convidado') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form v-model="formGuestValid" ref="formGuest">
            <v-row no-gutters>
              <v-col cols="12">
                <v-text-field
                  outlined
                  dense
                  :label="$t('labels.documentos')"
                  :placeholder="$t('login.placeholder.documento')"
                  type="number"
                  class="text-caption ml-1"
                  v-model.trim="form.document"
                  :rules="formRules.documentRules"
                  @focusin="focusedRequest = !focusedRequest"
                  @focusout="focusedRequest = !focusedRequest"
                >
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="9">
                <v-text-field
                  outlined
                  dense
                  :label="$t('labels.email')"
                  :placeholder="$t('login.placeholder.email')"
                  class="text-caption ml-1"
                  v-model="form.email"
                  :rules="formRules.emailRules"
                  @focusin="focusedRequest = !focusedRequest"
                  @focusout="focusedRequest = !focusedRequest"
                >
                </v-text-field>
              </v-col>
              <v-col cols="3">
                <v-btn
                  color="warning"
                  @click="requestAuthentication"
                  :loading="sendingRequest"
                  @shortkey="requestAuthentication"
                  v-shortkey.once="focusedRequest ? ['enter'] : undefined"
                  @focusin="focusedRequest = !focusedRequest"
                  @focusout="focusedRequest = !focusedRequest"
                >
                  <span class="small text-button font-weight-regular">
                    {{ $t('labels.enviar') }}
                  </span>
                  <v-icon color="white" small right>
                    mdi-email-send
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-show="tokenSend">
              <v-col cols="12">
                <v-text-field
                  outlined
                  dense
                  :placeholder="
                    $t('login.token-message', { email: form.email })
                  "
                  :label="$t('login.placeholder.token')"
                  class="text-caption ml-1"
                  v-model="form.guestToken"
                  :rules="formRules.required"
                  @focusin="focusedLogin = !focusedLogin"
                  @focusout="focusedLogin = !focusedLogin"
                >
                </v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn dark color="red lighten-1" @click="$emit('closeGuestLogin')">
            <span>{{ $t('labels.fechar') }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            dark
            color="teal lighten-1"
            @click="loginGuest"
            :disabled="!tokenSend"
            @shortkey="loginGuest()"
            v-shortkey.once="focusedLogin ? ['enter'] : undefined"
            @focusin="focusedLogin = !focusedLogin"
            @focusout="focusedLogin = !focusedLogin"
          >
            <span>{{ $t('labels.acessar') }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </megabit-base-dialog>
</template>

<script>
import BaseDialog from '@/components/shared/base-dialog/BaseDialog'
import { mapActions } from 'vuex'
import { emailFormat } from '@/utils/utils'

export default {
  components: { 'megabit-base-dialog': BaseDialog },
  data: () => {
    return {
      focusedRequest: false,
      focusedLogin: false,
      formGuestValid: false,
      tokenSend: false,
      sendingRequest: false,
      form: {
        document: '',
        email: '',
        guestToken: '',
      },
    }
  },
  computed: {
    formRules() {
      const rules = {
        required: [(v) => !!v || this.$t('validation-messages.requiredField')],
        emailRules: [emailFormat(true)],
        documentRules: [
          (v) => !!v || this.$t('validation-messages.requiredField'),
          (v) =>
            v.length >= 6 ||
            this.$t('validation-messages.minLength', {
              quantidade: 6,
            }),
          (v) =>
            v.length <= 14 ||
            this.$t('validation-messages.maxLength', {
              quantidade: 14,
            }),
        ],
      }

      return rules
    },
  },
  methods: {
    ...mapActions({
      loginGuestUser: 'authModule/loginGuestUser',
      sendEmail: 'authModule/sendEmail',
      showLoadingDialog: 'dialogModule/setShowLoadingDialog',
      setLoadingDialogMessage: 'dialogModule/setLoadingDialogMessage',
    }),
    async requestAuthentication() {
      try {
        this.$refs.formGuest.validate()
        if (!this.form.email || !this.form.document) {
          throw this.$t('login.corrigir')
        }

        this.setLoadingDialogMessage(
          this.$t('login.verificandoAcesso', { email: this.form.email })
        )
        this.showLoadingDialog(true)
        this.sendingRequest = !this.sendingRequest
        let response = await this.sendEmail({
          doc: this.form.document,
          email: this.form.email,
        })

        if (response.type === 'Notification')
          this.$toasted.info(
            this.$t('login.contaNaoConfirmada', { email: this.form.email }),
            { duration: null }
          )
        else if (response.type === 'Authorization') {
          this.tokenSend = true
          this.$toasted.info(response.status, { duration: null })
        } else this.$toasted.info(response.status, { duration: null })
      } catch (error) {
        this.$toasted.error(error, { duration: null })
      } finally {
        this.showLoadingDialog(false)
        this.sendingRequest = false
      }
    },
    async loginGuest() {
      try {
        this.$refs.formGuest.validate()
        if (!this.formGuestValid) throw this.$t('login.corrigir')

        this.setLoadingDialogMessage(this.$t('login.autenticando'))
        this.showLoadingDialog(true)

        let loginData = {
          doc: this.form.document,
          email: this.form.email,
          token: this.form.guestToken,
        }

        await this.loginGuestUser(loginData)
        this.$toasted.info(this.$t('login.autenticado'))

        this.$router.push({ name: 'Home' })
      } catch (error) {
        this.$toasted.error(error, { duration: null })
      } finally {
        this.showLoadingDialog(false)
        this.$emit('closeGuestDialog')
      }
    },
  },
  props: {
    showGuestDialog: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
}
</script>
